import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  IconButton,
  CardActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Delete, Edit, Cancel, Save } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  getReciprocityDetail,
  patchRCRoles,
  postRCRoles,
  replaceExistingRCRole,
  deleteRCRoles,
  getReciprocityPositions,
  addRCChapter,
  removeRCChapter,
  patchReciprocity,
} from '../../endpoints/chapters';
import dayjs from 'dayjs';
import { roles } from '../../utils/variables';
import PositionDisplay from '../../Components/PositionDisplay';
import AddPosition from '../../Components/AddPosition';
import ChapterSelector from '../../Components/ChapterSelector';
import { reciprocityTypes } from '../../utils/variables';

export default function ReciprocityDetail() {
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);
  const [chapterError, setChapterError] = useState(null);
  const [localChapterSearch, setLocalChapterSearch] = useState(null);
  const [localChapter, setLocalChapter] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [reciprocityError, setReciprocityError] = useState(null);

  const resetState = () => {
    setErrorSaving(null);
    setChapterError(null);
    setLocalChapterSearch(false);
    setLocalChapter(null);
  };

  const {
    data: reciprocityData,
    isLoading,
    refetch,
  } = useApiGet(
    ['reciprocity-detail', id],
    getReciprocityDetail,
    {
      enabled: true,
      retry: 1,
    },
    {
      id,
    }
  );

  const { data: reciprocityPositions } = useApiGet(
    ['reciprocity-positions'],
    getReciprocityPositions,
    {
      enabled: true,
      retry: 1,
    },
    {}
  );

  const { mutate: updateRoles } = useApiSend(
    patchRCRoles,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteRoles } = useApiSend(
    deleteRCRoles,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );
  const { mutate: postRoles } = useApiSend(
    postRCRoles,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceRoles } = useApiSend(
    replaceExistingRCRole,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: addChapter } = useApiSend(
    addRCChapter,
    (data) => {
      setIsSaving(false);
      setLocalChapterSearch(false);
      setLocalChapter(null);
      setChapterError(null);
      refetch();
    },
    (error) => {
      setChapterError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteChapter } = useApiSend(
    removeRCChapter,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setChapterError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: updateReciprocity } = useApiSend(
    patchReciprocity,
    (data) => {
      setIsSaving(false);
      setIsEditing(false);
      setReciprocityError(null);
      refetch();
    },
    (error) => {
      setReciprocityError(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const handleEdit = () => {
    setIsEditing(true);
    setName(reciprocityData.name);
    setType(reciprocityData.reciprocity_type_id);
    setStatus(reciprocityData.is_active);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setName(reciprocityData.name);
    setType(reciprocityData.reciprocity_type_id);
    setStatus(reciprocityData.is_active);
  };

  const handleSave = () => {
    setIsSaving(true);
    setErrorSaving(null);
    updateReciprocity({
      formId: id,
      name: name,
      reciprocity_type_id: type,
      is_active: status,
    });
  };

  const onSavePosition = (position) => {
    setIsSaving(true);
    setErrorSaving(null);
    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        postRoles({
          formId: id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
          role_id: position.positionId,
        });
      } else {
        replaceRoles({
          formId: id,
          id: position.id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
        });
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      updateRoles({
        formId: id,
        id: position.id,
        start_date: position.startDate,
        end_date: position.endDate,
      });
    } else if (position.action === 'removeRecord') {
      //Handle delete
      deleteRoles({
        formId: id,
        id: position.id,
      });
    }
  };

  const onAddChapter = () => {
    setIsSaving(true);
    setErrorSaving(null);
    addChapter({
      id: id,
      chapterId: localChapter,
    });
  };

  const onDeleteChapter = (chapterId) => {
    setIsSaving(true);
    setErrorSaving(null);
    deleteChapter({
      id: id,
      chapterId: chapterId,
    });
  };

  const canEdit = () => {
    let userRole = localStorage.getItem('role');
    if (reciprocityData?.spd_access) {
      let rolesAllowed = [roles.Admin, roles.Support, roles.SPDOfficer];
      return rolesAllowed.includes(userRole);
    } else if ([roles.Admin, roles.Support].includes(userRole)) {
      return false;
    } else {
      let userID = localStorage.getItem('user_id');
      let access = reciprocityData?.contacts.find(
        (contact) => contact.user_id === userID
      );
      return access ? true : false;
    }
  };

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }
  if (!reciprocityData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} padding paddingTop={5}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={reciprocityData.name}
            subheader={reciprocityData.is_active ? 'Active' : 'Inactive'}
            sx={{ p: 1 }}
          />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding>
                    <TextField
                      label="Name"
                      fullWidth={true}
                      size="small"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      sx={{ textAlign: 'left' }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6} padding>
                    <Typography variant="body1">
                      <b>{'Name:'}</b> {reciprocityData.name}
                    </Typography>
                  </Grid>
                </>
              )}
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding>
                    <FormControl size={'small'} fullWidth={true}>
                      <InputLabel id="type-label">Type</InputLabel>

                      <Select
                        label="Type"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                      >
                        <MenuItem value={reciprocityTypes.Reciprocity}>
                          Reciprocity
                        </MenuItem>
                        <MenuItem value={reciprocityTypes.Group}>
                          Group
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6} padding>
                    <Typography variant="body1">
                      <b>{'Type:'}</b>{' '}
                      {reciprocityData.reciprocity_type_id ===
                      reciprocityTypes.Reciprocity
                        ? 'Reciprocity'
                        : 'Group'}
                    </Typography>
                  </Grid>
                </>
              )}
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding>
                    <FormControl size={'small'} fullWidth={true}>
                      <InputLabel id="status-label">Status</InputLabel>

                      <Select
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6} padding>
                    <Typography variant="body1">
                      <b>{'Status:'}</b>{' '}
                      {reciprocityData.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <b>{'S/P/D:'}</b>{' '}
                  {reciprocityData.spd_chapter_name
                    ? reciprocityData.spd_chapter_name
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <b>{'Date Started:'}</b>{' '}
                  {reciprocityData.start_date
                    ? dayjs(reciprocityData.start_date).format('MM/DD/YYYY')
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} padding>
                <Typography variant="body1">
                  <b>{'Date Ended:'}</b>{' '}
                  {reciprocityData.end_date
                    ? dayjs(reciprocityData.end_date).format('MM/DD/YYYY')
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          {canEdit() && (
            <CardActions>
              <Grid container>
                {isEditing ? (
                  <>
                    <Grid item xs={12} padding textAlign={'right'}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCancel}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Cancel sx={{ fontSize: 17, pr: 1 }} />
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="green"
                        onClick={handleSave}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Save sx={{ fontSize: 17, pr: 1 }} />
                        Save
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} padding textAlign={'right'}>
                      <Button
                        variant="contained"
                        color="blue"
                        onClick={handleEdit}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Edit sx={{ fontSize: 17, pr: 1 }} />
                        Edit
                      </Button>
                    </Grid>
                  </>
                )}
                {reciprocityError && (
                  <Grid item xs={12} padding textAlign={'center'}>
                    <Typography variant="body1" color="red">
                      {reciprocityError}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={'Contacts'} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              <Grid item xs={12} padding>
                {reciprocityData.contacts
                  .sort((a, b) => a.order - b.order)
                  .map((user) => (
                    <PositionDisplay
                      key={user.id}
                      id={user.id}
                      position={user.role}
                      positionId={user.role_id}
                      committeeId={null}
                      name={user.name}
                      startDate={user.start_date}
                      endDate={user.end_date}
                      canEdit={canEdit()}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={onSavePosition}
                      userId={user.user_id}
                      pageFrom={'reciprocity'}
                    />
                  ))}
              </Grid>
              {canEdit() && (
                <AddPosition
                  buttonText={'Add Position'}
                  isSaving={isSaving}
                  positions={reciprocityPositions}
                  onAdd={onSavePosition}
                  savingError={errorSaving}
                />
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={'Local Chapters'} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {reciprocityData.chapters
                .sort((a, b) => a.order - b.order)
                .map((localChapter) => (
                  <Grid item xs={12} padding key={localChapter.id}>
                    <Typography variant="body1">
                      <b>{'Name:'}</b> {localChapter.chapter_name}
                      <IconButton
                        onClick={() => onDeleteChapter(localChapter.chapter_id)}
                        sx={{ padding: 0, marginLeft: 1 }}
                        disabled={isSaving}
                      >
                        <Delete
                          fontSize="small"
                          color="primary"
                          sx={{ fontSize: 18 }}
                        />
                      </IconButton>
                    </Typography>
                  </Grid>
                ))}
              {reciprocityData.chapters.length === 0 && (
                <Grid item xs={12} padding textAlign={'center'}>
                  <Typography variant="body1">
                    No local chapters assigned
                  </Typography>
                </Grid>
              )}
              {canEdit() && (
                <Grid item container xs={12} padding>
                  {localChapterSearch ? (
                    <Grid item xs={12} textAlign={'center'} paddingTop={2}>
                      <ChapterSelector
                        chapter={localChapter}
                        handleChapterSelect={(chapterId) =>
                          setLocalChapter(chapterId)
                        }
                        spd={null}
                        reportId={id}
                        label={'Select Local Chapter'}
                      />
                      {localChapter && (
                        <Button
                          variant="contained"
                          color="green"
                          onClick={() => onAddChapter(localChapterSearch.id)}
                          size="small"
                          disabled={isSaving}
                          sx={{
                            fontWeight: 'bold',
                            marginTop: 2,
                          }}
                        >
                          <Add sx={{ fontSize: 17, pr: 1 }} />
                          Add Local Chapter
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => resetState()}
                        size="small"
                        disabled={isSaving}
                        sx={{
                          fontWeight: 'bold',
                          marginTop: 2,
                          marginLeft: 2,
                        }}
                      >
                        Cancel
                      </Button>
                      {chapterError && (
                        <Typography variant="body1" color="red">
                          {chapterError}
                        </Typography>
                      )}
                    </Grid>
                  ) : (
                    <Grid item xs={12} textAlign={'center'}>
                      <Button
                        variant="contained"
                        color="green"
                        onClick={() => setLocalChapterSearch(true)}
                        size="small"
                        disabled={isSaving}
                        sx={{
                          fontWeight: 'bold',
                          marginTop: 2,
                        }}
                      >
                        <Add sx={{ fontSize: 17, pr: 1 }} />
                        Add Local Chapter
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
